import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, CheckCircle2, IndianRupee, Loader2, RefreshCw } from 'lucide-react';

const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export const FinanceAuditTab = ({ onOpenUser }) => {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('open');
  const [busy, setBusy] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const load = useCallback(() => {
    api.get('/api/admin/audit/finance', { params: { status, limit: 50 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Could not load financial audit'));
  }, [status]);

  useEffect(() => { setData(null); load(); }, [load]);

  const review = async (id, action) => {
    setBusy(true);
    try {
      await api.post(`/api/admin/audit/finance/${id}/review`, { action });
      toast.success('Flag closed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update flag');
    }
    setBusy(false);
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      const r = await api.post('/api/admin/audit/finance/rescan');
      toast.success(`Checked ${r.data.checked} payments · ${r.data.flagged} new flag(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rescan failed');
    }
    setRescanning(false);
  };

  const flags = data?.flags || [];

  return (
    <div className="space-y-4" data-testid="finance-audit-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <IndianRupee className="h-4 w-4" />
            Currency mismatches
            {data?.open_count > 0 && (
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200" data-testid="finance-audit-open-count">
                {data.open_count} open
              </Badge>
            )}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Every international learner charged in INR (and every India learner charged in USD) is
            flagged here the moment the payment lands — from the LMS checkout, an OPS payment link
            or the website. The founder's office is emailed automatically for each one.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="finance-audit-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={rescan} disabled={rescanning} data-testid="finance-audit-rescan-btn">
              {rescanning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Re-check past payments
            </Button>
          </div>

          {!data ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : flags.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8" data-testid="finance-audit-empty">
              No currency mismatch found. Tap "Re-check past payments" to scan the full history.
            </p>
          ) : (
            <div className="space-y-3" data-testid="finance-audit-list">
              {flags.map((f) => (
                <div key={f.id} className="p-4 rounded-xl border" data-testid={`finance-audit-row-${f.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-medium flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-rose-600" />
                        <button
                          className="underline decoration-dotted hover:text-primary text-left"
                          onClick={() => onOpenUser?.(f.user_id)}
                          data-testid={`finance-audit-learner-${f.id}`}
                        >
                          {f.learner_name || f.learner_email || f.user_id}
                        </button>
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                          {f.kind === 'international_paid_inr' ? 'International paid in INR' : 'India charged in USD'}
                        </Badge>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Paid <strong>{f.currency} {f.amount}</strong> · should have been <strong>{f.expected_currency}</strong>
                        {f.purpose ? ` · ${f.purpose}` : ''}{f.invoice_number ? ` · ${f.invoice_number}` : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Charged via <strong>{f.payment_source}</strong> · {fmt(f.paid_at)}
                        {f.ops_lead_id ? ` · OPS lead ${f.ops_lead_id}` : ''}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Resolved country <strong>{f.resolved_country}</strong> ({f.resolution_reason}, {f.resolution_confidence} confidence)
                        {(f.country_flags || []).length ? ` · flags: ${f.country_flags.join(', ')}` : ''}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Signals — {Object.entries(f.signals || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}
                        {f.device_signals?.device_timezone ? ` · clock ${f.device_signals.device_timezone}` : ''}
                      </p>
                      {f.status === 'reviewed' && (
                        <p className="text-[11px] text-emerald-700 mt-1">
                          Closed as {f.review_action} {f.reviewed_at ? `on ${fmt(f.reviewed_at)}` : ''}
                        </p>
                      )}
                    </div>
                    {f.status === 'open' && (
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => review(f.id, 'refund_issued')} data-testid={`finance-audit-refund-${f.id}`}>
                          Refunded
                        </Button>
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => review(f.id, 'recharged')} data-testid={`finance-audit-recharged-${f.id}`}>
                          Re-charged correctly
                        </Button>
                        <Button size="sm" variant="ghost" disabled={busy}
                          onClick={() => review(f.id, 'false_positive')} data-testid={`finance-audit-dismiss-${f.id}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Not an issue
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
